import React, { useState, useEffect } from 'react';
import styles from '../capitalExpenditure.module.scss';
import { 
  isNepaliInput, 
  isEnglishInput, 
  convertNepaliToEnglishDigits, 
  convertEnglishToNepaliDigits,
  formatWithAutoCommas
} from '../utils/numberUtils';

const InputField = ({
  value,
  onChange,
  placeholder,
  readOnly,
  viewMode,
  className = '',
  type = 'text',
  isNumeric = false
}) => {
  const [displayValue, setDisplayValue] = useState(value || '');
  const [digitFormat, setDigitFormat] = useState('english'); // 'english' or 'nepali'
  const [isMounted, setIsMounted] = useState(true);
  
    const inputClassName = `${styles.inputField} ${viewMode ? styles.readOnly : ''} ${className}`.trim();
  
  // Component mount/unmount effect
  useEffect(() => {
    setIsMounted(true);
    
    return () => {
      setIsMounted(false);
    };
  }, []);
  
  
  // Detect digit format from input
  const detectDigitFormat = (inputValue) => {
    if (isNepaliInput(inputValue)) {
      return 'nepali';
    } else if (isEnglishInput(inputValue)) {
      return 'english';
    }
    return digitFormat; // Keep current format if no digits detected
  };
  
  // Handle input change with automatic format detection
  const handleInputChange = (e) => {
    // Add null checks to prevent errors when component is unmounting
    if (!e || !e.target || !isMounted) {
      return;
    }
    
    const inputValue = e.target.value;
    
    // Safety check for onChange function
    if (typeof onChange !== 'function') {
      return;
    }
    
    if (isNumeric && inputValue) {
      // Detect the digit format being used
      const newFormat = detectDigitFormat(inputValue);
      if (isMounted) {
        setDigitFormat(newFormat);
      }
      
      // Convert to English for internal storage (calculations)
      let englishValue = inputValue;
      if (newFormat === 'nepali') {
        englishValue = convertNepaliToEnglishDigits(inputValue);
      }
      
      // Format with commas based on detected format
      const formattedValue = formatWithAutoCommas(englishValue);
      
      // Update display with formatted value in user's preferred format
      if (newFormat === 'nepali') {
        if (isMounted) {
          setDisplayValue(convertEnglishToNepaliDigits(formattedValue));
        }
      } else {
        if (isMounted) {
          setDisplayValue(formattedValue);
        }
      }
      
      // Call parent onChange with English digits for calculations
      try {
        onChange(e.target.value, englishValue);
      } catch (error) {
        console.warn('Error in onChange callback:', error);
      }
    } else {
      // Non-numeric field, just update normally
      if (isMounted) {
        setDisplayValue(inputValue);
      }
      // For non-numeric fields, pass the same value for both parameters to avoid errors
      try {
        onChange(e.target.value, e.target.value);
      } catch (error) {
        console.warn('Error in onChange callback:', error);
      }
    }
  };
  
  // Handle real-time updates for immediate calculation display
  const handleKeyUp = (e) => {
    // Add null checks to prevent errors when component is unmounting
    if (!e || !e.target || !isMounted) {
      return;
    }
    
    if (isNumeric && e.target.value) {
      // Trigger immediate calculation update
      const inputValue = e.target.value;
      const newFormat = detectDigitFormat(inputValue);
      
      if (newFormat !== digitFormat && isMounted) {
        setDigitFormat(newFormat);
        // Force a re-render to update the format immediately
        setDisplayValue(prev => prev);
      }
    }
  };
  
  // Handle real-time format detection and update
  const handleInput = (e) => {
    // Add null checks to prevent errors when component is unmounting
    if (!e || !e.target || !isMounted) {
      return;
    }
    
    if (isNumeric && e.target.value) {
      const inputValue = e.target.value;
      const newFormat = detectDigitFormat(inputValue);
      
      if (newFormat !== digitFormat && isMounted) {
        setDigitFormat(newFormat);
        // Update display immediately with new format
        if (newFormat === 'nepali') {
          const englishValue = convertNepaliToEnglishDigits(inputValue);
          const formattedValue = formatWithAutoCommas(englishValue);
          if (isMounted) {
            setDisplayValue(convertEnglishToNepaliDigits(formattedValue));
          }
        } else {
          const formattedValue = formatWithAutoCommas(inputValue);
          if (isMounted) {
            setDisplayValue(formattedValue);
          }
        }
      }
    }
  };
  
  // Update display value when value prop changes
  useEffect(() => {
    let alive = true;

    const raw = value === null || value === undefined ? '' : String(value);
    if (!alive) return undefined;

    if (isNumeric && raw !== '') {
      const detectedFormat = detectDigitFormat(raw) || digitFormat;
      setDigitFormat(detectedFormat);
      if (detectedFormat === 'nepali') {
        const englishValue = convertNepaliToEnglishDigits(raw);
        const formattedValue = formatWithAutoCommas(englishValue);
        setDisplayValue(convertEnglishToNepaliDigits(formattedValue));
      } else {
        setDisplayValue(formatWithAutoCommas(raw));
      }
    } else {
      setDisplayValue(raw);
    }

    return () => {
      alive = false;
    };
  }, [value, isNumeric]);
  
  // Handle blur event to ensure proper formatting
  const handleBlur = () => {
    if (isNumeric && displayValue && isMounted) {
      const cleanValue = displayValue.replace(/,/g, '');
      const formattedValue = formatWithAutoCommas(cleanValue);
      
      if (digitFormat === 'nepali') {
        if (isMounted) {
          setDisplayValue(convertEnglishToNepaliDigits(formattedValue));
        }
      } else {
        if (isMounted) {
          setDisplayValue(formattedValue);
        }
      }
    }
  };
  
  return (
    <input
      type={type}
      value={displayValue}
      onChange={handleInputChange}
      onInput={handleInput}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
      className={inputClassName}
      placeholder={placeholder}
      readOnly={readOnly}
    />
  );
};

export default React.memo(InputField);
